import { ErrorDialog } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { parseValidationError } from 'services/ErrorManagement';
import { post } from 'services/Http';

const fileToBase64 = async (fileToUpload) => {
    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    return {
        file: await toBase64(fileToUpload),
    };
};

export const CapabilityFileUpload = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [lastError, setLastError] = useState(null);
    const [isError, errorTitle, errorDetails] = parseValidationError(lastError);

    const CapabilityTypeUpload = {
        ALL: 'LDD and State Capabilities',
        LDD: 'LDD Capabilities Only',
    };
    const uploadEndpointMap = {
        [CapabilityTypeUpload.ALL]: `/reference/capabilities/${npi}/upload/base64`,
        [CapabilityTypeUpload.LDD]: `/reference/capabilities/${npi}/ldd-upload/base64`,
    };

    function clearError() {
        setLastError(null);
    }

    return (
        <Formik
            initialValues={{ file: null }}
            onSubmit={async (values, { resetForm }) => {
                setIsLoading(true);
                try {
                    const body = await fileToBase64(values.fileToUpload);
                    const response = await post(uploadEndpointMap[values.capabilityType], body);
                    if (response?.supplier_npi) {
                        SuccessToast({ message: 'Capability file uploaded successfully.' });
                        resetForm();
                    } else {
                        setLastError({
                            errorTitle: 'Something went wrong',
                            errorDetails: 'No NPI returned - there was a problem.',
                        });
                    }
                } catch (error) {
                    setLastError(error);
                }
                setIsLoading(false);
            }}
            validationSchema={Yup.object({
                capabilityType: Yup.string().required(validationStrings.requiredField.errorMessage),
                file: Yup.mixed().required(validationStrings.requiredField.errorMessage),
            })}
        >
            {(formik) => (
                <>
                    <ErrorDialog
                        dialogTitle={errorTitle}
                        isOpen={isError}
                        onSecondaryButtonClick={() => clearError()}
                        dialogContent={errorDetails}
                    />
                    <FormContainer
                        formTitle="Upload Supplier Data Collect File"
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={formik.isSubmitting || !formik.isValid || !formik.dirty}
                        onSecondaryButtonClick={onSecondaryButtonClick ?? formik.resetForm}
                        isLoading={isLoading}
                        formLeftContent={
                            <div
                                className="flex__gap--small"
                                style={{
                                    'flex-flow': 'column wrap',
                                    'align-content': 'flex-start',
                                }}
                            >
                                <RadioButtonGroupFormik
                                    id="capabilityType"
                                    name="capabilityType"
                                    label="What data would you like to upload from that file:"
                                    options={Object.values(CapabilityTypeUpload)}
                                />
                                {formik.values.capabilityType && (
                                    <FileUploadFormik
                                        id="file"
                                        label="Data Collect File"
                                        maxAllowedFiles={1}
                                        onAddFiles={(files) =>
                                            formik.setFieldValue('fileToUpload', files?.[0])
                                        }
                                        onRemoveFile={() =>
                                            formik.setFieldValue('fileToUpload', undefined)
                                        }
                                    />
                                )}
                            </div>
                        }
                    />
                </>
            )}
        </Formik>
    );
};

CapabilityFileUpload.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
